import http from "@/axios/http-common";
import ErrorAlert from "@/utility/ErrorAlert";

const TIMEOUT_MS = 300000;

export type JenisPeriode = "bulan" | "triwulan" | "semester" | "tahun";
export type DasarTt = "real" | "sk";
export type JenisBaris =
  | "grup"
  | "bangsal"
  | "judul"
  | "sub"
  | "tambahan"
  | "jumlah"
  | "total";
export type KolomIndikator = "bor" | "los" | "toi" | "bto" | "ndr" | "gdr";

export interface JumlahLp {
  L: number;
  P: number;
}

export interface HpKelas {
  vip: number;
  k1: number;
  k2: number;
  k3: number;
  iso: number;
  tk: number;
  tidak_tercatat: number;
}

export type KelasBor = "vip" | "k1" | "k2" | "k3" | "tk" | "iso";

export interface BorKelasBaris {
  label: string;
  hari: number;
  tt: Record<KelasBor, number> & { jumlah: number; tidak_tercatat: number };
  hp: Record<KelasBor, number>;
  bor: Record<KelasBor, number | null>;
  tt_x_hari: Record<KelasBor, number>;
}

export interface BorKelas {
  kolom: KelasBor[];
  rows: BorKelasBaris[];
  total: BorKelasBaris | null;
  catatan: string[];
}

export interface IndikatorBaris {
  no: string;
  label: string;
  units: string[];
  jenis: JenisBaris;
  grup: string;
  hari: number | null;
  awal: number | null;
  masuk: JumlahLp | null;
  pindahan: JumlahLp | null;
  jumlah_pasien: number | null;
  dirawat: (JumlahLp & { jumlah: number }) | null;
  keluar_hidup: JumlahLp | null;
  dipindah: JumlahLp | null;
  mati: { lt48: number; ge48: number; jumlah: number } | null;
  keluar: { hidup: number; mati: number; jumlah: number } | null;
  tt: number | null;
  tt_ada: boolean;
  tt_kelas: HpKelas | null;
  akhir: number | null;
  hp_kelas: HpKelas | null;
  hp: number | null;
  ld: number | null;
  tt_x_hari: number | null;
  tt_x_hari_kurang_hp: number | null;
  bor: number | null;
  los: number | null;
  toi: number | null;
  bto: number | null;
  ndr: number | null;
  gdr: number | null;
  tersembunyi: KolomIndikator[];
}

export interface IndikatorPeriode {
  tahun: number;
  jenis: JenisPeriode;
  ke: number;
  kode: string;
  label: string;
  mulai: string;
  selesai: string;
  as_of: string;
  berjalan: boolean;
  hari: number;
  jumlah_bulan: number;
  tgl_tt: string;
  dasar_tt: DasarTt;
  dasar_tt_label: string;
  unit: string[];
}

export interface IndikatorRanapResponse {
  periode: IndikatorPeriode;
  rows: IndikatorBaris[];
  total: IndikatorBaris;
  catatan: {
    tanpa_tt: string[];
    tanpa_tt_berpasien: string[];
    unit_tambahan: string[];
    tanpa_tgl_lahir: number;
    kelas_tidak_tercatat: number;
    teks: string[];
  };
  bor_kelas: BorKelas;
}

export interface IndikatorFilter {
  tahun: number;
  jenis: JenisPeriode;
  ke: number;
  dasar_tt: DasarTt;
  unit?: string[];
}

function params(f: IndikatorFilter) {
  return {
    tahun: f.tahun,
    jenis: f.jenis,
    ke: f.ke,
    dasar_tt: f.dasar_tt,
    unit: f.unit
  };
}

async function ambilBlob(url: string, p: Record<string, unknown>) {
  return http
    .getDataParam(url, { params: p, responseType: "blob", timeout: TIMEOUT_MS })
    .catch(err => {
      ErrorAlert.showErrorAlert(err);
      return Promise.reject(err);
    });
}

function unduh(data: BlobPart, namaBerkas: string) {
  const url = URL.createObjectURL(
    new Blob([data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    })
  );
  const a = document.createElement("a");
  a.href = url;
  a.download = namaBerkas;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

function kodePeriode(f: IndikatorFilter): string {
  if (f.jenis === "bulan") return `${f.tahun}-${String(f.ke).padStart(2, "0")}`;
  if (f.jenis === "triwulan") return `${f.tahun}-TW${f.ke}`;
  if (f.jenis === "semester") return `${f.tahun}-SMT${f.ke}`;
  return String(f.tahun);
}

async function preview(f: IndikatorFilter): Promise<IndikatorRanapResponse> {
  const res = await http
    .getDataParam("/laporan/rp1-ranap/indikator-ranap", {
      params: params(f),
      timeout: TIMEOUT_MS
    })
    .catch(err => {
      ErrorAlert.showErrorAlert(err);
      return Promise.reject(err);
    });
  return res.data;
}

async function openPdf(f: IndikatorFilter): Promise<void> {
  const res = await ambilBlob("/laporan/rp1-ranap/indikator-ranap/pdf", params(f));
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
  window.open(url, "_blank");
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

async function downloadExcel(f: IndikatorFilter): Promise<void> {
  const res = await ambilBlob("/laporan/rp1-ranap/indikator-ranap/excel", params(f));
  unduh(res.data, `rp1-indikator-ranap_${kodePeriode(f)}_${f.dasar_tt}.xlsx`);
}

async function downloadExcelTahunan(tahun: number, unit?: string[]): Promise<void> {
  const res = await ambilBlob("/laporan/rp1-ranap/indikator-ranap/excel-tahunan", {
    tahun,
    unit
  });
  unduh(res.data, `rp1-indikator-ranap_rekap-${tahun}.xlsx`);
}

export default { preview, openPdf, downloadExcel, downloadExcelTahunan };
